<template>
    <div class="campaign-box campaign-box--no-top-border campaign-box--top-padding" data-dusk="payment-history">
        <div class="col-md-12 text-center" v-if="loading">
            <i class="fa fa-spinner fa-spin inner-loading"></i>
        </div>

        <div v-else>
            <div class="row">
                <div class="col-sm-4">
                    <p class="m-b-lg text-muted">
                        This chart allows you to review your credit card funding history.
                    </p>

                    <div class="form-group">
                        <label class="control-label">Product Category</label>

                        <select class="form-control" data-dusk="fundsPoolId" v-model="fundsPoolId" :disabled="refreshing">
                            <option :value="fundsPool.id" v-for="fundsPool in fundsPools" :key="fundsPool.id">{{ fundsPool.name }}</option>
                        </select>

                    </div>

                    <div v-if="showAccountBalance">
                        <h3 style="margin-bottom:0px;">${{ Number(accountBalanceCents) / 100 | currency }}</h3>
                        <p class="text-muted">
                            {{ accountName }} Account Balance
                        </p>
                    </div>
                </div>

                <div class="col-sm-8">
                    <!-- Dim Table Overlay -->
                    <div class="funding-history-overlay" v-if="refreshing"></div>

                    <table class="table table-striped table-condensed table-responsive" v-if="showConvenienceFee()">
                        <thead>
                        <tr>
                            <th>Funding Source</th>
                            <th>Funded Amount</th>
                            <th>Fee</th>
                            <th>Total</th>
                            <th>Date</th>
                        </tr>
                        </thead>

                        <tbody>
                        <tr v-for="(payment, index) in payments" :key="index">
                            <td>{{ payment.fundingSourceName }}</td>
                            <td>${{ Number(payment.amountCents) / 100 | currency }}</td>
                            <td>${{ Number(payment.convenienceFeeCents) / 100 | currency }}</td>
                            <td>${{ (Number(payment.amountCents) + Number(payment.convenienceFeeCents)) / 100 | currency }}</td>
                            <td>{{ formatTimestamp(payment.date) }} <span class="text-muted">ET</span></td>
                        </tr>

                        <tr v-if="!payments.length">
                            <td class="text-center" colspan="3">
                                <span class="text-muted">No funding history for {{ accountName }} account</span>
                            </td>
                        </tr>

                        <tr v-if="morePaymentsAreAvailable">
                            <td class="text-center" colspan="5">
                                <a class="btn btn-link" href="#" @click.prevent="showMoreResults" :disabled="showMoreResultsLoading">Show More</a>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <table class="table table-striped table-condensed table-responsive" v-else>
                        <thead>
                            <tr>
                                <th>Funding Source</th>
                                <th>Amount</th>
                                <th>Date</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="(payment, index) in payments" :key="index">
                                <td>{{ payment.fundingSourceName }}</td>
                                <td>${{ Number(payment.amountCents) / 100 | currency }}</td>
                                <td>{{ formatTimestamp(payment.date) }} <span class="text-muted">ET</span></td>
                            </tr>

                            <tr v-if="!payments.length">
                                <td class="text-center" colspan="3">
                                    <span class="text-muted">No funding history for {{ accountName }} account</span>
                                </td>
                            </tr>

                            <tr v-if="morePaymentsAreAvailable">
                                <td class="text-center" colspan="3">
                                    <a class="btn btn-link" href="#" @click.prevent="showMoreResults" :disabled="showMoreResultsLoading">Show More</a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.funding-history-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.1);
    margin-left: -15px;
}
</style>

<script type="text/babel">
    import moment from 'moment';
    import Swal from "sweetalert2";

    export default {
        props: {
            fundsPools: Array
        },

        data() {
            return {
                loading: true,
                refreshing: false,
                payments: [],
                accountName: '',
                accountBalanceCents: null,
                fundsPool: {},
                fundsPoolId: 0,
                showAccountBalance:false,
                showMoreResultsIncrement: 20,
                showMoreResultsLimit: 20,
                showMoreResultsLoading: false,
            }
        },

        mounted() {
            this.fundsPool = this.fundsPools[0];
            this.fundsPoolId = this.fundsPool.id;

            Promise.all([
                this.getAccountBalance(),
                this.getPayments()
            ]).then(() => {
                this.loading = false;
            });
        },

        watch: {
            fundsPoolId() {
                if (this.loading) return
                this.loading = true;

                this.fundsPool = this.fundsPools.find(fundsPool => { return fundsPool.id === this.fundsPoolId })
                this.getPayments().then(() => { this.loading = false; });

                this.getAccountBalance();
            }
        },

        computed: {
            morePaymentsAreAvailable() {
                return this.payments.length === this.showMoreResultsLimit;
            }
        },

        methods: {
            getPayments() {
                return this.$http.get(route('billing-setup.payment.index'), {params: {fundsPoolId: this.fundsPoolId, limit: this.showMoreResultsLimit}})
                    .then(({ data }) => {
                        this.payments = data.payments;
                    }).catch((error) => {
                        new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error');
                    });
            },

            getAccountBalance() {
                this.showAccountBalance = false;
                return this.$http.get(route('api.advertiser.account-balance.query', this.fundsPool.id))
                  .then(response => {
                      this.accountBalanceCents = response.data.balanceCents;
                      this.accountName =  this.fundsPool.name;
                      this.showAccountBalance = true;
                  });
            },

            showMoreResults() {
                this.showMoreResultsLimit += this.showMoreResultsIncrement;

                this.refreshing = true;

                this.getPayments()
                    .then(() => {
                        this.refreshing = false;
                    });
            },

            showConvenienceFee() {
                return this.featureFlag('billing.show-convenience-fee') === true
            },

            formatTimestamp(timestamp) {
                return moment.unix(timestamp).format('MM/DD/YYYY hh:mma')
            }
        }
    }
</script>
